/**
 * Spares — the two halves of the same list.
 *
 *   Be a spare    add yourself to a league's spare list, and come off it again
 *   Find a spare  see who is on it, and how to reach them
 *
 * No new API: `league_registrations.is_spare` has been the model since
 * migration 001, `POST /leagues/:id/registrations` lets a member register
 * THEMSELVES (`canActForMember`, so not anybody else), and
 * `GET /leagues/:id/spares` returns the list with a phone number and an email.
 *
 * Two things the server enforces that this screen has to explain rather than
 * work around: a league whose registration is closed refuses the write, and
 * so does one where the member has no membership for the season. Saying so
 * plainly is better than a disabled button with no reason on it.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DAYS, type League } from '@lcc/shared';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Card, Empty, ErrorNote, Loading, Pill, useToast } from '../components/ui';
import { PageHeader } from '../App';

interface Registration {
  id: string;
  memberId: string;
  fullName: string;
  positionPref: string | null;
  isSpare: boolean;
  phone?: string | null;
  email?: string | null;
}

const POSITIONS = ['skip', 'vice', 'second', 'lead', 'alternate'] as const;

export function SparesPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const [leagueId, setLeagueId] = useState<string | null>(null);
  const [position, setPosition] = useState<string>('');

  const leagues = useQuery({
    queryKey: ['leagues'],
    queryFn: () => api.get<{ data: League[] }>('/leagues').then((r) => r.data),
  });

  const active = leagueId ?? leagues.data?.[0]?.id ?? null;
  const league = leagues.data?.find((l) => l.id === active);

  const spares = useQuery({
    queryKey: ['spares', active],
    queryFn: () => api.get<{ data: Registration[] }>(`/leagues/${active}/spares`).then((r) => r.data),
    enabled: Boolean(active),
  });

  const meOnList = spares.data?.find((s) => s.memberId === user?.memberId);

  const join = useMutation({
    mutationFn: () => api.post(`/leagues/${active}/registrations`, {
      memberId: user!.memberId,
      isSpare: true,
      entryMode: 'single',
      positionPref: position || null,
      skillRating: null,
      teamId: null,
      notes: null,
    }),
    onSuccess: () => {
      toast.success(`You are on the spare list for ${league?.name ?? 'that league'}.`);
      void qc.invalidateQueries({ queryKey: ['spares', active] });
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'That did not work.'),
  });

  const leave = useMutation({
    mutationFn: () => api.del(`/leagues/${active}/registrations/${user!.memberId}`),
    onSuccess: () => {
      toast.success('Taken off the spare list.');
      void qc.invalidateQueries({ queryKey: ['spares', active] });
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'That did not work.'),
  });

  return (
    <>
      <PageHeader
        title="Spares"
        sub="Put your name down, or find somebody to fill a line-up"
      />
      <div className="content stack">
        {leagues.isLoading && <Loading />}
        {leagues.error && <ErrorNote error={leagues.error} />}

        {leagues.data && leagues.data.length > 0 && (
          <div className="tabs" role="tablist" aria-label="League">
            {leagues.data.map((l) => (
              <button
                key={l.id}
                type="button"
                role="tab"
                aria-selected={l.id === active}
                onClick={() => setLeagueId(l.id)}
              >
                {l.name}
              </button>
            ))}
          </div>
        )}

        {league && (
          <Card title={`Be a spare — ${league.name}`}>
            <p className="small muted" style={{ marginTop: 0 }}>
              {league.drawTimes.length
                ? league.drawTimes
                    .map((d) => `${DAYS[d.dayOfWeek] ?? ''} ${d.startTime}`)
                    .join(' · ')
                : 'Draw times to be confirmed'}
            </p>

            {meOnList ? (
              <>
                <p>
                  You are on this spare list
                  {meOnList.positionPref && <> as <b>{meOnList.positionPref}</b></>}.
                  Conveners and skips can see your name and how to reach you.
                </p>
                <button
                  className="btn btn--danger btn--sm"
                  type="button"
                  disabled={leave.isPending}
                  onClick={() => leave.mutate()}
                >
                  {leave.isPending ? 'Removing…' : 'Take me off the list'}
                </button>
              </>
            ) : (
              <>
                <p>
                  Add yourself and you will be called when a team is short. Nothing is
                  scheduled for you and there is no commitment to turn up.
                </p>
                <div className="field" style={{ maxWidth: '18rem' }}>
                  <label htmlFor="spare-position">Position you would rather play</label>
                  <select
                    id="spare-position"
                    value={position}
                    onChange={(e) => setPosition(e.target.value)}
                  >
                    <option value="">No preference</option>
                    {POSITIONS.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
                <button
                  className="btn btn--primary btn--sm"
                  type="button"
                  disabled={join.isPending || !user?.memberId}
                  onClick={() => join.mutate()}
                >
                  {join.isPending ? 'Adding…' : 'Put me on the spare list'}
                </button>
                {!league.registrationOpen && (
                  <p className="small muted" style={{ marginBottom: 0 }}>
                    Registration for this league is closed, so the club office has to add you —
                    the button will tell you so if you try.
                  </p>
                )}
              </>
            )}
          </Card>
        )}

        <Card title={league ? `Find a spare — ${league.name}` : 'Find a spare'}>
          {spares.isLoading && <Loading what="Loading the spare list" />}
          {spares.error && <ErrorNote error={spares.error} />}
          {spares.data && spares.data.length === 0 && (
            <Empty title="Nobody on this list yet">
              Be the first — adding yourself above is what puts a name here.
            </Empty>
          )}
          {spares.data && spares.data.length > 0 && (
            <div className="table-wrap">
              <table className="t">
                <caption className="sr-only">Spares for this league</caption>
                <thead>
                  <tr><th>Name</th><th>Plays</th><th>Phone</th><th>Email</th></tr>
                </thead>
                <tbody>
                  {spares.data.map((sp) => (
                    <tr key={sp.id}>
                      <td>
                        {sp.fullName}
                        {sp.memberId === user?.memberId && <> <Pill tone="info">you</Pill></>}
                      </td>
                      <td style={{ textTransform: 'capitalize' }}>{sp.positionPref ?? 'any'}</td>
                      <td className="nowrap">
                        {sp.phone ? <a className="link" href={`tel:${sp.phone}`}>{sp.phone}</a> : '—'}
                      </td>
                      <td>
                        {sp.email ? <a className="link" href={`mailto:${sp.email}`}>{sp.email}</a> : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}
